import type {
  Commonv1Value,
  V1AutomationInputSpec,
} from '@/generated/hooks/grimoire/models';
import type { V1DefaultInputsHistoryEntry } from '@/generated/hooks/grimoire/models/v1DefaultInputsHistoryEntry';
import type { InputComponentType, TFileHistoryVersion } from '../types';
import {
  getInputComponentType,
  getValueType,
  isFileListType,
  unwrapType,
} from './typeDetection';

/**
 * Maps value types returned by `getValueType` to their compatible
 * `InputComponentType` equivalents. Used to verify that a historical
 * value's actual type matches the current input spec's expected type.
 */
const VALUE_TYPE_TO_COMPONENT_TYPE: Record<string, InputComponentType[]> = {
  text: ['text', 'enum'],
  boolean: ['boolean'],
  number: ['number'],
  date: ['date'],
  datetime: ['datetime'],
  time: ['time'],
  file: ['file'],
  // A list value backs both the generic element editor and, for element types
  // without one, the JSON textarea. Deliberately not `'file'`: this module
  // resolves types with `getInputComponentType`, which returns `'complex'` for
  // any list, so `'file'` here would only let a stale list value masquerade as
  // a file version in `buildFileHistoryMap`.
  list: ['complex', 'list'],
  dictionary: ['complex'],
  table: ['complex'],
  set: ['complex'],
};

/**
 * Checks whether a `Commonv1Value`'s actual type is compatible with
 * the expected `InputComponentType` from the input spec.
 *
 * @param value - The history value to check
 * @param expectedType - The expected input component type from the spec
 * @returns Whether the value's type is compatible
 */
const isTypeCompatible = (
  value: Commonv1Value,
  expectedType: InputComponentType,
): boolean => {
  const actualType = getValueType(value);
  if (!actualType) return false;

  const compatibleTypes = VALUE_TYPE_TO_COMPONENT_TYPE[actualType];
  if (!compatibleTypes) return false;

  return compatibleTypes.includes(expectedType);
};

/**
 * Checks whether a `Commonv1Value` is null, undefined, an empty object
 * (no recognised property set), or a null-type value (`null_value` is set).
 *
 * @param value - The value to check
 * @returns Whether the value should be treated as absent
 */
const isNullOrEmptyValue = (value?: Commonv1Value): boolean => {
  if (!value) return true;
  if (value.null_value !== undefined) return true;
  const valueType = getValueType(value);
  return !valueType;
};

/**
 * Builds a map of input spec keys to their type-filtered historical values
 * with the most recent (first) entry removed since it represents the current value.
 *
 * For each input spec:
 * 1. Determines the expected component type from the spec's `V1ValueType`
 * 2. Walks through all history entries (newest first) and extracts values
 *    for that input key
 * 3. Discards values that are null, empty objects, or null-type
 * 4. Discards values whose type doesn't match the spec
 *
 * @param inputSpecs - Current input specifications
 * @param historyEntries - All history entries (expected newest first from API)
 * @returns A record mapping each input name to its historical values (latest removed)
 */
export const buildInputHistoryMap = (
  inputSpecs: V1AutomationInputSpec[],
  historyEntries: V1DefaultInputsHistoryEntry[],
): Record<string, Commonv1Value[]> => {
  const result: Record<string, Commonv1Value[]> = {};

  for (const spec of inputSpecs) {
    const { name, type } = spec;
    if (!name) continue;

    const { baseType } = unwrapType(type);
    const expectedComponentType = getInputComponentType(baseType);

    const values: Commonv1Value[] = [];

    for (const entry of historyEntries) {
      const value = entry.default_inputs?.[name];

      if (!value || isNullOrEmptyValue(value)) continue;

      if (!isTypeCompatible(value, expectedComponentType)) continue;

      values.push(value);
    }

    result[name] = values;
  }

  return result;
};

/**
 * Builds a file-specific history map for single-file inputs.
 *
 * For each input spec whose resolved component type is `'file'` and is NOT
 * a file-list type, this function walks through the history entries
 * (newest first), extracts the file value, and assigns version labels
 * ("v1", "v2", ...) from oldest to newest.
 *
 * Entries that are null, empty objects, or null-type values
 * are skipped. Keys that end up with no history items are omitted.
 *
 * @param inputSpecs - Current input specifications
 * @param historyEntries - All history entries (expected newest first from API)
 * @returns A record mapping each single-file input name to its versioned history
 */
export const buildFileHistoryMap = (
  inputSpecs: V1AutomationInputSpec[],
  historyEntries: V1DefaultInputsHistoryEntry[],
): Record<string, TFileHistoryVersion[]> => {
  const result: Record<string, TFileHistoryVersion[]> = {};

  for (const spec of inputSpecs) {
    const { name, type } = spec;
    if (!name) continue;

    const { baseType } = unwrapType(type);
    const componentType = getInputComponentType(baseType);

    // Only process single-file inputs (not file lists)
    if (componentType !== 'file' || isFileListType(baseType)) continue;

    // Collect items newest-first (same order as historyEntries)
    const newestFirst: { value: Commonv1Value; changeTime?: string }[] = [];

    for (const entry of historyEntries) {
      const value = entry.default_inputs?.[name];

      if (!value || isNullOrEmptyValue(value)) continue;
      if (!isTypeCompatible(value, 'file')) continue;

      newestFirst.push({ value, changeTime: entry.change_time });
    }

    if (newestFirst.length === 0) continue;

    // Reverse to oldest-first so we can assign ascending version labels
    const oldestFirst = [...newestFirst].reverse();
    const versioned: TFileHistoryVersion[] = oldestFirst.map((item, idx) => ({
      value: item.value,
      changeTime: item.changeTime,
      versionLabel: `v${idx + 1}`,
    }));

    // Reverse back to newest-first for display
    result[name] = versioned.reverse();
  }

  return result;
};
